import { useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAuthentication from "../../hooks/useAuthentication";
import {
  addExperience,
  updateExperience,
  deleteExperience,
  searchInstitutions,
} from "../../utility/httpActions/userActions";

const ExperienceDialog = ({ open, onClose, experience = null }) => {
  const isEdit = !!experience;
  const queryClient = useQueryClient();
  const { setCurrentUser } = useAuthentication();

  const [title, setTitle] = useState(experience?.title || "");
  const [organization, setOrganization] = useState(experience?.organisation || experience?.organization || "");
  const [startYear, setStartYear] = useState(experience?.startYear?.toString() || "");
  const [endYear, setEndYear] = useState(experience?.endYear?.toString() || "");
  const [isCurrent, setIsCurrent] = useState(experience?.isCurrent || false);
  const [description, setDescription] = useState(experience?.description || "");
  const [snackbar, setSnackbar] = useState(null);

  const [orgOptions, setOrgOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);

  useEffect(() => {
    if (organization.length < 2) { setOrgOptions([]); return; }
    const timer = setTimeout(async () => {
      setOrgLoading(true);
      try {
        const results = await searchInstitutions({ q: organization, type: "workplace" });
        setOrgOptions(results.map((r) => r.label));
      } catch {
        setOrgOptions([]);
      }
      setOrgLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [organization]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    queryClient.invalidateQueries({ queryKey: ["publicUser"] });
    queryClient.invalidateQueries({ queryKey: ["userShops"] });
  };

  const onSuccess = (data) => {
    if (data?.user) setCurrentUser(data.user);
    invalidate();
    onClose();
  };

  const onError = (err) => {
    setSnackbar({ severity: "error", message: err?.data?.message || "Something went wrong." });
  };

  const { mutate: doAdd, isPending: adding } = useMutation({ mutationFn: addExperience, onSuccess, onError });
  const { mutate: doUpdate, isPending: updating } = useMutation({ mutationFn: updateExperience, onSuccess, onError });
  const { mutate: doDelete, isPending: deleting } = useMutation({ mutationFn: deleteExperience, onSuccess, onError });

  const isSaving = adding || updating || deleting;

  const handleSave = () => {
    const payload = {
      title: title.trim(),
      organization: organization.trim(),
      startYear: parseInt(startYear, 10),
      endYear: isCurrent ? null : (endYear ? parseInt(endYear, 10) : null),
      isCurrent,
      description: description.trim() || undefined,
    };
    if (isEdit) {
      doUpdate({ experienceId: experience._id || experience.id, ...payload });
    } else {
      doAdd(payload);
    }
  };

  const handleDelete = () => {
    if (window.confirm("Delete this experience?")) {
      doDelete(experience._id || experience.id);
    }
  };

  const valid = title.trim() && organization.trim() && startYear && !isNaN(parseInt(startYear, 10));

  return (
    <>
      <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEdit ? "Edit experience" : "Add experience"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <TextField
            label="Title / Role"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            size="small"
            disabled={isSaving}
          />
          <Autocomplete
            freeSolo
            options={orgOptions}
            loading={orgLoading}
            inputValue={organization}
            onInputChange={(_, val) => setOrganization(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Company / Organisation"
                required
                size="small"
                disabled={isSaving}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {orgLoading && <CircularProgress size={16} sx={{ color: "#9ca3af" }} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Start year"
              value={startYear}
              onChange={(e) => setStartYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
              required
              size="small"
              sx={{ flex: 1 }}
              disabled={isSaving}
              inputProps={{ inputMode: "numeric" }}
            />
            <TextField
              label="End year"
              value={isCurrent ? "" : endYear}
              onChange={(e) => setEndYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
              size="small"
              sx={{ flex: 1 }}
              disabled={isSaving || isCurrent}
              inputProps={{ inputMode: "numeric" }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={isCurrent}
                onChange={(e) => setIsCurrent(e.target.checked)}
                size="small"
                sx={{ "&.Mui-checked": { color: "#046287" } }}
              />
            }
            label={<Typography variant="body2">I currently work here</Typography>}
            disabled={isSaving}
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            disabled={isSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: isEdit ? "space-between" : "flex-end" }}>
          {isEdit && (
            <Button color="error" onClick={handleDelete} disabled={isSaving}>
              Delete
            </Button>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!valid || isSaving}
              sx={{ bgcolor: "#046287", "&:hover": { bgcolor: "#035070" } }}
            >
              {isSaving ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Save"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default ExperienceDialog;
